/**
 * Core type definitions for MJJ Trading Tool
 * Requirements: 1.1, 3.3, 4.1, 5.1
 */

/** 商品類型 */
export type CategoryType = 'vps' | 'vds' | 'hosting' | 'account' | 'domain';

/** 硬碟配置 */
export interface DiskConfig {
  size: number;
  unit: 'GB' | 'TB';
  type: 'SSD' | 'NVMe' | 'HDD' | 'SAS';
}

/** 流量配置 */
export interface TrafficConfig {
  isUnlimited: boolean;
  value: number;
  unit: 'GB' | 'TB';
  direction: '雙向計算' | '只算入站' | '只算出站';
  speed: number;
  speedUnit: 'Mbps' | 'Gbps';
  throttleInfo: string;
}

/** 聯繫方式 */
export interface ContactInfo {
  tg: string;
  xy: string;
  ns: string; // Nodeseek ID
}

/** 卡片資料 */
export interface CardData {
  // Category
  category: CategoryType;

  // Basic Info
  provider: string;
  productName: string;
  region: string;
  remarks: string;

  // Specs (Server only)
  cpu: string;
  ram: string;
  disks: DiskConfig[];
  routeType: string;
  customRoute: string;

  // Traffic
  traffic: TrafficConfig;

  // Finance
  currency: 'USD' | 'EUR' | 'CNY' | 'TWD';
  renewalPrice: number;
  sellingPrice: number;
  billingCycle: number; // 0 = lifetime, 30 = monthly, 365 = yearly, 730 = 2 years
  purchaseDate: string;
  nextDueDate: string;
  exchangeRate: number;
  targetCurrency: 'TWD' | 'CNY' | 'HKD' | 'USD';

  // Contact
  contact: ContactInfo;
}

/** 計算結果 */
export interface CalculationResult {
  remainingDays: number;
  residualValue: number;
  residualValueTarget: number;
  sellingPriceTarget: number;
  premium: number;
  isPremium: boolean;
  progress: number;
}

/** 預設卡片資料 */
export const defaultCardData: CardData = {
  category: 'vps',
  provider: 'DMIT',
  productName: 'PVM.LAX.Pro.WEE',
  region: 'Los Angeles',
  remarks: '',
  cpu: '1 vCPU AMD EPYC',
  ram: '1 GB',
  disks: [{ size: 10, unit: 'GB', type: 'SSD' }],
  routeType: 'CN2 GIA 優化',
  customRoute: '',
  traffic: {
    isUnlimited: false,
    value: 1000,
    unit: 'GB',
    direction: '雙向計算',
    speed: 1,
    speedUnit: 'Gbps',
    throttleInfo: '',
  },
  currency: 'USD',
  renewalPrice: 36.9,
  sellingPrice: 50,
  billingCycle: 365,
  purchaseDate: '',
  nextDueDate: '',
  exchangeRate: 32.5,
  targetCurrency: 'TWD',
  contact: { tg: '@mjj_trader', xy: '', ns: '' },
};

/** 預設計算結果 */
export const defaultCalculationResult: CalculationResult = {
  remainingDays: 0,
  residualValue: 0,
  residualValueTarget: 0,
  sellingPriceTarget: 0,
  premium: 0,
  isPremium: false,
  progress: 0,
};

/** 線路類型選項 */
export const routeOptions = [
  'CN2 GIA 優化',
  'CU 9929 優化',
  'CMIN2 優化',
  '三網直連',
  '國際線路 (BGP)',
  '中轉節點',
  '落地解鎖',
  'custom',
] as const;

/** 計費週期選項 */
export const billingCycleOptions = [
  { value: 30, label: '月付 (30天)' },
  { value: 365, label: '年付' },
  { value: 730, label: '兩年付' },
  { value: 0, label: '終身' },
] as const;

/** 幣種選項 */
export const currencyOptions = ['USD', 'EUR', 'CNY', 'TWD'] as const;

/** 目標幣種選項 */
export const targetCurrencyOptions = ['TWD', 'CNY', 'HKD', 'USD'] as const;
